import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface Props {
  pendingId: string;
}

export default function KakaoEmailCollect({ pendingId }: Props) {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuthStore();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/auth/kakao/complete-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pendingId, email, name }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }

      // Fetch full user profile so the store gets all required fields
      const meRes = await fetch(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${data.token}` },
        credentials: 'include',
      });
      if (meRes.ok) {
        const meData = await meRes.json();
        setToken(data.token);
        setUser(meData.user);
      }

      navigate('/');
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-8">
          {/* Close / escape */}
          <div className="flex justify-end mb-2">
            <Link
              to="/"
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Back to home"
            >
              <X className="w-4 h-4" />
            </Link>
          </div>

          {/* Kakao logo */}
          <div className="flex justify-center mb-6">
            <div className="w-14 h-14 bg-[#FEE500] rounded-full flex items-center justify-center">
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="#3A1D1D">
                <path d="M12 3C6.477 3 2 6.463 2 10.8c0 2.756 1.838 5.175 4.608 6.554-.165.608-.6 2.205-.687 2.545-.108.418.153.412.323.3.133-.088 2.116-1.396 2.971-1.967.91.136 1.852.208 2.785.208 5.523 0 10-3.463 10-7.84S17.523 3 12 3z" />
              </svg>
            </div>
          </div>

          <h1 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
            One more step
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            Your Kakao account doesn't have an email address. Please provide one to finish signing up.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email address <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#FEE500]"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Display name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#FEE500]"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            )}

            <button
              type="submit"
              disabled={isLoading || !email}
              className="w-full py-2.5 bg-[#FEE500] hover:bg-yellow-400 disabled:opacity-50 text-gray-900 font-semibold rounded-lg transition"
            >
              {isLoading ? 'Saving…' : 'Continue'}
            </button>
          </form>

          <p className="mt-4 text-center text-xs text-gray-400">
            Having trouble?{' '}
            <a href="/login" className="underline">
              Use email & password instead
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
